import { useCallback, useMemo } from 'react';
import { AccountResource, OrderCurrencySourceCurrencyWithBalance } from '../../resources/account.resource.js';
import {
  CashInfoByCurrency,
  Currency,
  CurrencyToBalanceMapping,
  ExchangePeriodHoursType,
  ListedInstrument,
  SelectedAccount,
} from '../../types/index.js';
import { isDefined } from '../../utils/objectUtils.js';
import { Loadable, LoadableState } from '../../utils/useLoadableState.js';
import {
  defaultGetRefreshTimes,
  useRefreshingExchangeCurrencies,
} from '../currency-exchange/useRefreshingExchangeCurrencies.js';
import { GetResourceHeaderWithTokenForAccount } from '../useResource.js';

type UseOrderExchangeActiveBalancesCommonParams = {
  selectedAccount: SelectedAccount | undefined;
  cashInfo: LoadableState<CashInfoByCurrency>;
  instrument: ListedInstrument | undefined | null;
  exchangePeriodHours: ExchangePeriodHoursType;
};

export type UseOrderExchangeActiveBalancesCommonPlatformParams = {
  accountResource: AccountResource;
  getResourceHeaderWithTokenForAccount: GetResourceHeaderWithTokenForAccount;
};

type UseOrderExchangeActiveBalancesOutput = {
  balances: LoadableState<OrderCurrencySourceCurrencyWithBalance[]>;
  balancesByCurrency: LoadableState<CurrencyToBalanceMapping>;
  individuallyLoadingBalances: LoadableState<OrderCurrencySourceCurrencyWithBalance>[];
  // TODO this is not the right place for this, but we need a separate API that tells if exchange is active or not.
  isCurrencyExchangeActive: LoadableState<boolean>;
  loadActiveBalances: () => Promise<void>;
  startRefreshActiveBalances: () => void;
  stopRefreshActiveBalances: () => void;
  resetActiveBalances: () => void;
};

type SourceCurrenciesData = {
  mic: string;
  targetCcyCode: string;
  accountId: string;
  cashInfo: CashInfoByCurrency;
};

export const useOrderExchangeActiveBalancesCommon = (
  { selectedAccount, instrument, cashInfo, exchangePeriodHours }: UseOrderExchangeActiveBalancesCommonParams,
  { accountResource, getResourceHeaderWithTokenForAccount }: UseOrderExchangeActiveBalancesCommonPlatformParams,
): UseOrderExchangeActiveBalancesOutput => {
  const accountId = selectedAccount?.account?.accountNumber;

  const input = useMemo((): Partial<SourceCurrenciesData> => {
    return {
      mic: instrument?.mic,
      targetCcyCode: instrument?.currency,
      accountId: accountId,
      cashInfo: Loadable.hasValue(cashInfo) ? cashInfo : undefined,
    };
  }, [accountId, cashInfo, instrument?.currency, instrument?.mic]);

  const load = useCallback(
    async ({
      accountId,
      mic,
      targetCcyCode,
      cashInfo,
    }: SourceCurrenciesData): Promise<OrderCurrencySourceCurrencyWithBalance[]> => {
      const { data } = await accountResource.getSourceCurrenciesWithBalance(
        { mic, targetCcyCode },
        getResourceHeaderWithTokenForAccount(accountId),
      );
      return data.filter(({ currency }) => currency !== targetCcyCode).sort(compareByInvestableHufDesc(cashInfo));
    },
    [accountResource, getResourceHeaderWithTokenForAccount],
  );

  const {
    data: rawBalances,
    refresh,
    startRefreshing,
    stopRefreshing,
    reset,
  } = useRefreshingExchangeCurrencies<
    OrderCurrencySourceCurrencyWithBalance,
    Partial<SourceCurrenciesData>,
    SourceCurrenciesData
  >({
    input,
    exchangePeriodHours,
    load,
    getRefreshTimes: defaultGetRefreshTimes,
    isInputValid: isSourceCurrenciesDataValid,
  });

  const balances = useMemo<LoadableState<OrderCurrencySourceCurrencyWithBalance[]>>(() => {
    return Loadable.map(rawBalances, rawBalances =>
      rawBalances.filter(({ investableAmount }) => isDefined(investableAmount) && investableAmount >= 1),
    );
  }, [rawBalances]);

  // TODO this is not the right place for this, but we need a separate API that tells if exchange is active or not.
  const isCurrencyExchangeActive = useMemo<LoadableState<boolean>>(
    () => Loadable.map(rawBalances, rawBalances => rawBalances.length > 0),
    [rawBalances],
  );

  const balancesByCurrency = useMemo(() => {
    return Loadable.map(
      balances,
      (balances): Record<string, OrderCurrencySourceCurrencyWithBalance> =>
        balances.reduce(
          (balancesMap, balance) => ({ ...balancesMap, [balance.currency]: balance }),
          {} as Record<string, OrderCurrencySourceCurrencyWithBalance>,
        ),
    );
  }, [balances]);

  // This is purely cosmetic. We guesstimate the amount of currencies based on cashInfo to allow rendering a similar amount of "loading" elements
  const individuallyLoadingBalances = useMemo<LoadableState<OrderCurrencySourceCurrencyWithBalance>[]>(() => {
    // Probably can't happen, but better to be safe
    if (!isDefined(instrument?.currency)) {
      return [];
    }
    // If balances has value, we are loaded, we simply unwrap the balances loadable
    if (Loadable.hasValue(balances)) {
      return balances.map(balance => Loadable.loaded(balance));
    }
    // If balance does not have a value but cashInfo does, we return an array with the cashInfo currencies (except targetCurrency) loading
    if (Loadable.hasValue(cashInfo)) {
      const cashInfoCurrencies = Object.keys(cashInfo.cashInfo).filter(currency => currency !== instrument.currency);
      // balances at this point are either uninitialized, failed or loading (hasValue === false), so it's safe to reuse.
      return cashInfoCurrencies.map(() => balances);
    }
    return [];
  }, [balances, cashInfo, instrument?.currency]);

  return {
    balances,
    balancesByCurrency,
    individuallyLoadingBalances,
    isCurrencyExchangeActive,
    loadActiveBalances: refresh,
    startRefreshActiveBalances: startRefreshing,
    stopRefreshActiveBalances: stopRefreshing,
    resetActiveBalances: reset,
  };
};

const isSourceCurrenciesDataValid = (input: Partial<SourceCurrenciesData>): input is SourceCurrenciesData => {
  const { accountId, cashInfo, mic, targetCcyCode } = input;
  return isDefined(accountId) && isDefined(mic) && isDefined(targetCcyCode) && isDefined(cashInfo);
};

const compareByInvestableHufDesc =
  (cashInfo: CashInfoByCurrency) =>
  (a: OrderCurrencySourceCurrencyWithBalance, b: OrderCurrencySourceCurrencyWithBalance): number => {
    const aHuf = cashInfo.cashInfo[a.currency as Currency]?.investableHuf ?? 0;
    const bHuf = cashInfo.cashInfo[b.currency as Currency]?.investableHuf ?? 0;
    return bHuf - aHuf;
  };
